/* eslint-disable max-lines */
import { HighlightOutlined, HistoryOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons'
import { useSuspenseQuery } from '@apollo/client'
import { FetchRecordChangeHistoriesDocument, type ViewFieldsFragment } from '@salescore/client-api'
import { getOrganizationIdFromPath } from '@salescore/client-base'
import { SuspenseWithLoading, useMe, UserAvatar } from '@salescore/client-common'
import { selectGetSalesforceUser, useAppSelector } from '@salescore/client-redux'
import type {
  CoreDslFormNodeState,
  CoreDslFormTerm,
  SheetCell,
  ViewQueryList,
  ViewQueryRecordNode,
} from '@salescore/core'
import type { OrganizationSetting } from '@salescore/features'
import { organizationHasFeature } from '@salescore/frontend-common'
import { App, Divider, Popover } from 'antd'
import { Array, Option } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type CSSProperties, type ReactElement, type ReactNode, useEffect, useRef, useState } from 'react'
import { useLongPress } from 'react-use'

import type { RSheetRecordNode, SheetColumn } from '../../../domain/column.ts'
import { getPropertyTypeFromCoreDslTerm } from '../../../domain/coreDslFormTerm.ts'
import type { RSheetsCursorPosition } from '../../../domain/cursor/cursorTypes.ts'
import { getCoreDslFormOperatorLabel } from '../../../domain/operatorLabel.ts'
import { useViewQueryResult } from '../../../hooks/useViewQueryResult.ts'
import { useCursorMutation } from '../../../recoil/cursor.ts'
import { useRecordsImpl } from '../../../recoil/records.ts'
import { useAddEmptyRecordMutation } from '../../../recoil/records/mutations/addEmptyRecordMutation.ts'
import type { SalescoreObjectAbility, ViewAbility } from '../../../recoil/view/atoms.ts'
import { useGetModel, useGetModelProperty } from '../../../recoil/view/selectors/connectionsSelector.ts'
import { openContextMenu, setEditMode, toggleEditMode } from '../../../rsheet/components/body/cell/cellAction.ts'
import { highlightRibonMapper } from '../../../rsheet/components/modal/HighlightFormModal.tsx'
import type { SetMainCursorByRecordIdArguments } from '../../../rsheet/hooks/useSetMainCursorByRecordId.ts'
import { isCellWriteProhibited } from '../../../rsheet/util/cellWriteProhibition.ts'
import { getActiveConditionalEffects } from '../../../rsheet/util/conditionalEffects.ts'
import { getEmptyRecordCellBackgroundColor, RSheetsStyle } from '../../../rsheet/util/RSheetsStyle.ts'
import { SheetValueCellBody } from './SheetValueCellBody.tsx'

export const SheetValueCell = ({
  sheetCell,
  rootRecordNode,
  column,
  rowIndex,
  columnIndex,
  isNewRecord,
  isFieldChanged,
  overrideValue,
  rowHeight,
  setMainCursorByRecordId,
  view,
  setDragging,
  expandOnDrag,
  query,
  ability,
}: {
  sheetCell: SheetCell
  rootRecordNode: RSheetRecordNode
  column: SheetColumn<ViewQueryRecordNode>
  rowIndex: number
  columnIndex: number
  isNewRecord: boolean
  isFieldChanged: boolean
  overrideValue?: unknown
  rowHeight: number
  setMainCursorByRecordId: (arguments_: SetMainCursorByRecordIdArguments) => void
  view: ViewFieldsFragment
  setDragging: () => void
  expandOnDrag: (position: RSheetsCursorPosition) => void
  query: ViewQueryList
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  const { innerRowIndexStart, height, recordNode, value } = sheetCell
  const innerRowIndex = innerRowIndexStart
  const { width } = column
  const snapshots = useViewQueryResult(view)?.snapshots ?? {}
  const snapshot = snapshots[column.node.name]?.[recordNode?.id ?? ''] ?? {}

  const defaultStyle: CSSProperties = {
    width,
    height: height * rowHeight,
    position: 'relative',
    overflow: 'hidden',
    transition: RSheetsStyle.cellTransition,
    border: `1px ${RSheetsStyle.color.border} solid`,
  }

  if (recordNode === undefined) {
    return (
      <SheetValueEmptyCell
        column={column}
        rowIndex={rowIndex}
        columnIndex={columnIndex}
        innerRowIndex={innerRowIndexStart}
        defaultStyle={defaultStyle}
        query={query}
        setDragging={setDragging}
        expandOnDrag={expandOnDrag}
        ability={ability}
      />
    )
  }

  return (
    <RecordCell
      recordNode={recordNode}
      column={column}
      rowIndex={rowIndex}
      columnIndex={columnIndex}
      innerRowIndex={innerRowIndex}
      rootRecordNode={rootRecordNode}
      setMainCursorByRecordId={setMainCursorByRecordId}
      isFieldChanged={isFieldChanged}
      isNewRecord={isNewRecord}
      overrideValue={overrideValue}
      height={height}
      value={value}
      defaultStyle={defaultStyle}
      snapshot={snapshot}
      setDragging={setDragging}
      expandOnDrag={expandOnDrag}
      query={query}
      ability={ability}
    />
  )
}

const RecordCell = ({
  recordNode,
  column,
  rowIndex,
  columnIndex,
  innerRowIndex,
  rootRecordNode,
  setMainCursorByRecordId,
  isFieldChanged,
  isNewRecord,
  overrideValue,
  height,
  value,
  defaultStyle,
  snapshot,
  setDragging,
  expandOnDrag,
  query,
  ability,
}: {
  recordNode: RSheetRecordNode
  column: SheetColumn<ViewQueryRecordNode>
  rowIndex: number
  columnIndex: number
  innerRowIndex: number
  rootRecordNode: RSheetRecordNode
  setMainCursorByRecordId: (arguments_: SetMainCursorByRecordIdArguments) => void
  isFieldChanged: boolean
  isNewRecord: boolean
  overrideValue?: unknown
  height: number
  value: unknown
  defaultStyle: CSSProperties
  snapshot: Record<string, Record<string, unknown>>
  setDragging: () => void
  expandOnDrag: (position: RSheetsCursorPosition) => void
  query: ViewQueryList
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  const { organization } = useMe()

  const changed = isFieldChanged || isNewRecord
  const currentValue = changed ? overrideValue : value
  const { disabled, effects, style } = getEffectRelated(
    recordNode,
    changed,
    currentValue,
    column,
    snapshot,
    organization.setting,
  )
  const highlighted = effects.some((e) => e.effect === 'highlight')

  const mobileContextMenuEnabled = organizationHasFeature(organization, 'enable_mobile_context_menu')
  const longPressEventHandler = useLongPress(
    (event) => {
      if (!('touches' in event) || event.touches[0] === undefined) {
        return
      }
      const contextMenuPosition = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      }
      openContextMenu({
        rootRecordNode,
        columnIndex,
        innerRowIndex,
        contextMenuPosition,
        disabled,
        setMainCursorByRecordId,
      })
    },
    {
      isPreventDefault: false,
      delay: 300,
    },
  )

  const boxStyle: CSSProperties = {
    ...defaultStyle,
    padding: '10px 10px',
    ...style,
    fontSize: 14,
    ...(height === 1
      ? {
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }
      : {}),
  }
  const highlightColor = getActiveConditionalEffects(
    column.conditionalEffects,
    recordNode,
    snapshot,
    organization.setting,
  )[0]?.highlightColor
  // gray に fallback しているが、highlightColorMap は highlightColor に対応する値を持つように定義しているので fallback には入らない想定
  const highlightRibonColor =
    highlightColor === undefined ? 'rgb(209 213 219)' : (highlightRibonMapper.get(highlightColor) ?? 'rgb(209 213 219)')

  return (
    <div
      key={column.key}
      data-rsheet-cell="true"
      data-e2e={`rsheet-cell-${rowIndex}-${columnIndex}-${innerRowIndex}`}
      style={boxStyle}
      {...(mobileContextMenuEnabled ? longPressEventHandler : {})}
      onContextMenu={(event) => {
        const contextMenuPosition = {
          x: event.clientX,
          y: event.clientY,
        }
        openContextMenu({
          rootRecordNode,
          columnIndex,
          innerRowIndex,
          contextMenuPosition,
          disabled,
          setMainCursorByRecordId,
        })
        event.preventDefault()
        event.stopPropagation()
      }}
      onPointerEnter={() => {
        expandOnDrag({ rowIndex, columnIndex, innerRowIndex })
      }}
      onPointerDown={(event) => {
        setDragging() // event.stopPropagation()などによりsetDraggingが実行されなくなると、ドラッグによるセルの範囲選択が動作しなくなる。
        // ドラッグによるセルの範囲選択を実現するためには、クリック時にメインカーソルを更新する必要がある。
        // クリックを離す時に発火するonClickだと遅すぎるので、onPointerDownで処理する。
        if (event.shiftKey && rootRecordNode.id !== undefined) {
          setMainCursorByRecordId({
            position: {
              recordId: rootRecordNode.id,
              columnIndex,
              innerRowIndex,
            },
            option: {
              expand: true,
            },
          })
          return
        }
        toggleEditMode({ rootRecordNode, columnIndex, innerRowIndex, disabled, setMainCursorByRecordId })
      }}
    >
      {disabled && <img src="/images/sheets/write-inhibit-icon.svg" style={{ marginRight: 4 }} />}
      {highlighted && (
        <div className="absolute right-0 top-0 z-[1]">
          <Popover
            content={<HighlightInfo column={column} recordNode={recordNode} snapshot={snapshot} />}
            placement="right"
          >
            <div className="relative">
              <div
                // 右上向きの三角形
                style={{
                  borderTop: `12px solid ${highlightRibonColor}`,
                  borderRight: `12px solid ${highlightRibonColor}`,
                  borderBottom: '12px solid transparent',
                  borderLeft: '12px solid transparent',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                }}
              />
              <HighlightOutlined className="absolute right-0 top-0 size-3 text-white" />
            </div>
          </Popover>
        </div>
      )}
      <SheetValueCellBody
        column={column}
        value={currentValue}
        recordNode={recordNode}
        height={height}
        style={{
          color: boxStyle.color,
          backgroundColor: boxStyle.backgroundColor,
        }}
        setCursorAndUseEditMode={() => {
          setEditMode({ rootRecordNode, columnIndex, innerRowIndex, disabled, setMainCursorByRecordId })
        }}
        rowIndex={rowIndex}
        columnIndex={columnIndex}
        innerRowIndex={innerRowIndex}
        query={query}
        ability={ability}
      />
    </div>
  )
}

const mapper: Record<string, string> = {
  negative: 'red',
  positive: 'blue',
}

const getConditionalEffectHighlightColor = (color: string | undefined): string | undefined => {
  if (color === undefined) {
    return
  }

  return mapper[color] ?? color
}

const getEffectRelated = (
  recordNode: RSheetRecordNode,
  changed: boolean,
  value: unknown,
  column: SheetColumn<ViewQueryRecordNode>,
  snapshot: Record<string, Record<string, unknown>>,
  setting: OrganizationSetting,
  // eslint-disable-next-line @typescript-eslint/max-params
) => {
  const highlight = column.highlight(recordNode)

  const isValid =
    !column.required ||
    (value !== undefined &&
      value !== null &&
      (typeof value === 'string' ? value !== '' : !Array.isArray(value) || value.length > 0))
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: 同様の実装をuseHandleKeyDownでも必要
  const effects = getActiveConditionalEffects(column.conditionalEffects, recordNode, snapshot, setting)
  const disabled = effects.some((x) => x.effect === 'disable')
  const highlightedV2 = effects.some((x) => x.effect === 'highlight')
  const emphasised = effects.some((x) => x.effect === 'emphasis') || highlightedV2

  const getColorRelated = (): CSSProperties => {
    if (!isValid) {
      return { backgroundColor: RSheetsStyle.color.error }
    }

    if (changed) {
      return { backgroundColor: 'rgba(0,0,0,0.06)' }
    }

    if (highlightedV2) {
      return {
        color: 'white',
        backgroundColor: getConditionalEffectHighlightColor(
          effects.find((x) => x.effect === 'highlight')?.highlightColor,
        ),
      }
    }

    if (disabled) {
      return { backgroundColor: '#ddd' }
    }

    if (highlight === true) {
      return { backgroundColor: '#FEF9C3' }
    }

    return { backgroundColor: 'inherit' }
  }

  const style: CSSProperties = {
    ...getColorRelated(),
    fontWeight: emphasised ? `bold` : undefined,
    opacity: disabled ? 0.7 : 1,
  }

  return {
    style,
    effects,
    disabled,
  }
}

const HighlightInfo = ({
  column,
  recordNode,
  snapshot,
}: {
  column: SheetColumn<ViewQueryRecordNode>
  recordNode: RSheetRecordNode
  snapshot: Record<string, Record<string, unknown>>
}): ReactElement => {
  const activeConditionalEffect = getActiveConditionalEffects(column.conditionalEffects, recordNode, snapshot)[0]
  const conditionalEffect = column.configField?.conditionalEffects?.find(
    (ce) => ce.effect === 'highlight' && ce.highlightColor?.color === activeConditionalEffect?.highlightColor,
  )
  const ast = conditionalEffect?.effect === 'highlight' ? conditionalEffect.ast : undefined
  const astDeepKeyValues = getDeepKeyValues(ast ?? {})
  // ハイライト条件に「過去の値」を用いているかのフラグ
  const isComparingWithSnapshotVariable = astDeepKeyValues
    .filter((v) => v.includes('.type::snapshotVariable'))
    .map((v) => v.replace('.type::snapshotVariable', '.before'))
    .map((v1) => astDeepKeyValues.find((v2) => v2.includes(v1)))
    .filter(isNotNullish)
    .map((v) => v.split('::')[1])
    .some((v) => Number(v) > 0)

  const highlightConditionCount = astDeepKeyValues.filter((v) => v.includes('operator::')).length

  const recordId = recordNode.id
  const modelName = column.configField?.property.modelName
  const propertyName = column.configField?.property.propertyName

  const getModelProperty = useGetModelProperty()
  const property = getModelProperty(modelName, propertyName)

  const showRecordChangeHistory = property?.trackable === true && isComparingWithSnapshotVariable

  // eslint-disable-next-line sonarjs/use-type-alias
  const [highlightConditionViewState, setHighlightConditionViewState] = useState<'normal' | 'open' | 'close' | 'only'>(
    'normal',
  )
  const [recordChangeHistoryViewState, setRecordChangeHistoryViewState] = useState<'normal' | 'open' | 'close'>(
    'normal',
  )

  const [highlightConditionListNormalWidth, setHighlightConditionListNormalWidth] = useState(0)
  const [recordChangeHistoryListNormalWidth, setRecordChangeHistoryListNormalWidth] = useState(0)

  return (
    <div
      className="flex flex-col overflow-y-auto"
      style={{
        maxHeight: '60vh',
        minWidth: Math.max(highlightConditionListNormalWidth, recordChangeHistoryListNormalWidth),
      }}
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
    >
      {ast !== undefined && (
        <div>
          <div className="flex gap-2">
            <div className="font-bold">
              <HighlightOutlined />
            </div>
            <div className="font-bold">{t(`ハイライト条件`)}</div>
          </div>

          <HighlightConditionList
            ast={ast}
            count={highlightConditionCount}
            viewState={showRecordChangeHistory ? highlightConditionViewState : 'only'}
            onOpen={() => {
              setHighlightConditionViewState('open')
              setRecordChangeHistoryViewState('close')
            }}
            onClose={() => {
              setHighlightConditionViewState('normal')
              setRecordChangeHistoryViewState('normal')
            }}
            onSetNormalWidth={setHighlightConditionListNormalWidth}
          />
        </div>
      )}

      {showRecordChangeHistory && recordId !== undefined && modelName !== undefined && propertyName !== undefined && (
        <>
          <Divider style={{ margin: '12px 0' }} />

          <div>
            <div className="flex gap-2">
              <div className="font-bold">
                <HistoryOutlined />
              </div>
              <div className="font-bold">{t(`履歴`)}</div>
            </div>

            <SuspenseWithLoading type="table">
              <RecordChangeHistoryList
                recordId={recordId}
                modelName={modelName}
                propertyName={propertyName}
                viewState={recordChangeHistoryViewState}
                onOpen={() => {
                  setHighlightConditionViewState('close')
                  setRecordChangeHistoryViewState('open')
                }}
                onClose={() => {
                  setHighlightConditionViewState('normal')
                  setRecordChangeHistoryViewState('normal')
                }}
                onSetNormalWidth={setRecordChangeHistoryListNormalWidth}
              />
            </SuspenseWithLoading>
          </div>
        </>
      )}
    </div>
  )
}

const HighlightConditionList = ({
  ast,
  count,
  viewState,
  onOpen,
  onClose,
  onSetNormalWidth,
}: {
  ast: CoreDslFormNodeState
  count: number
  viewState: 'normal' | 'open' | 'close' | 'only'
  onOpen: () => void
  onClose: () => void
  onSetNormalWidth: (width: number) => void
}): ReactNode => {
  const reference = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (viewState === 'normal') {
      onSetNormalWidth(reference.current?.clientWidth ?? 0)
    }
  }, [viewState])

  if (viewState === 'close') {
    return null
  }

  return (
    <div className="mt-2 space-y-2" ref={reference}>
      <HighlightConditionListNode viewState={viewState} ast={ast} isRoot={true} />

      {count > 3 && (
        <>
          {viewState === 'normal' && (
            <div
              className="ml-1 max-w-max cursor-pointer text-xs font-semibold"
              onClick={() => {
                onOpen()
              }}
            >
              {t(`+{{count}}の条件を展開`, { count: count - 3 })}
            </div>
          )}

          {viewState === 'open' && (
            <div
              className="ml-1 max-w-max cursor-pointer text-xs font-semibold"
              onClick={() => {
                onClose()
              }}
            >
              {t(`閉じる`)}
            </div>
          )}
        </>
      )}
    </div>
  )
}

const HighlightConditionListNode = ({
  ast,
  isRoot,
  viewState,
}: {
  ast: CoreDslFormNodeState
  isRoot: boolean
  viewState: 'normal' | 'open' | 'close' | 'only'
}): ReactElement => {
  const leafs = {
    normal: isRoot ? ast.leafs.slice(0, 3) : ast.leafs.slice(0, 1),
    open: ast.leafs,
    close: [],
    only: ast.leafs,
  }[viewState]

  const children = {
    normal: isRoot ? ast.children.slice(0, 3 - leafs.length) : [],
    open: ast.children,
    close: [],
    only: ast.children,
  }[viewState]

  return (
    <div className={isRoot ? '' : 'rounded border border-solid border-gray-300 p-2'}>
      {leafs.map((leaf, index, ary) => (
        // eslint-disable-next-line @eslint-react/no-missing-key
        <>
          <div className="flex items-center rounded bg-gray-100 p-2 text-xs">
            <div className="min-w-20">
              <ValueLabelText term={leaf.left} />
            </div>
            <Divider type="vertical" />
            <div className="min-w-20">
              {getCoreDslFormOperatorLabel(t, leaf.operator, getPropertyTypeFromCoreDslTerm(leaf.left))}
            </div>
            {leaf.right !== undefined && (
              <>
                <Divider type="vertical" />
                <div className="min-w-20">
                  <ValueLabelText term={leaf.right} />
                </div>
              </>
            )}
          </div>

          {index !== ary.length - 1 && (
            <div className="px-2 py-1 text-xs capitalize text-gray-500">{ast.logicalOperator}</div>
          )}
        </>
      ))}

      {children.map((child) => (
        // eslint-disable-next-line @eslint-react/no-missing-key
        <>
          {ast.leafs.length > 0 && (
            <div className="px-2 py-1 text-xs capitalize text-gray-500">{ast.logicalOperator}</div>
          )}
          <HighlightConditionListNode viewState={viewState} ast={child} isRoot={false} />
        </>
      ))}
    </div>
  )
}

// eslint-disable-next-line sonarjs/function-return-type
const ValueLabelText = ({ term }: { term: CoreDslFormTerm }) => {
  const dateSpanDictionary = {
    day: t(`日`),
    week: t(`週間`),
    month: t(`月`),
    year: t(`年`),
  }
  const getModel = useGetModel()
  switch (term.type) {
    case 'literal': {
      return term.value
    }
    case 'snapshotVariable': {
      return term.before === 0 ? t(`現在の値`) : `${term.before}${dateSpanDictionary[term.dateSpan]}${t(`前の値`)}`
    }
    case 'recordNodeVariable': {
      return `${getModel(term.nodePropertyName.modelName)?.label}: ${term.property.label}`
    }
  }
}

const RecordChangeHistoryList = ({
  recordId,
  modelName,
  propertyName,
  viewState,
  onOpen,
  onClose,
  onSetNormalWidth,
}: {
  recordId: string
  modelName: string
  propertyName: string
  viewState: 'normal' | 'open' | 'close'
  onOpen: () => void
  onClose: () => void
  onSetNormalWidth: (width: number) => void
}): ReactElement => {
  const { data } = useSuspenseQuery(FetchRecordChangeHistoriesDocument, {
    variables: {
      recordId,
      modelName,
      propertyNames: [propertyName],
      organizationId: getOrganizationIdFromPath(),
    },
  })
  const { recordChangeHistories } = data

  const snapshots = {
    normal: recordChangeHistories.slice(0, 5),
    open: recordChangeHistories,
    close: [],
  }[viewState]

  const reference = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (viewState === 'normal') {
      onSetNormalWidth(reference.current?.clientWidth ?? 0)
    }
  }, [viewState])

  return (
    <div className="mt-2 space-y-2" ref={reference}>
      {snapshots.map((snapshot, index) => (
        <div key={`${snapshot.propertyName}_${index}`} className="flex items-center justify-between gap-5 text-xs">
          <div className="flex items-center gap-1">
            {index === 0 ? <div className="size-2 rounded-full bg-gray-200" /> : <div className="w-2"></div>}
            <div className="font-semibold text-gray-500">{snapshot.newValue}</div>
            <div className="text-gray-400">{t(`{{datetime, datetimestr}}`, { datetime: snapshot.changedAt })}</div>
          </div>

          <User salesforceId={snapshot.changedById} fallbackName={snapshot.changedByName ?? t(`不明なユーザー`)} />
        </div>
      ))}

      {recordChangeHistories.length > 5 && (
        <>
          {viewState === 'normal' && (
            <div
              className="ml-1 max-w-max cursor-pointer text-xs font-semibold"
              onClick={() => {
                onOpen()
              }}
            >
              {t(`展開`)}
            </div>
          )}

          {viewState === 'open' && (
            <div
              className="ml-1 max-w-max cursor-pointer text-xs font-semibold"
              onClick={() => {
                onClose()
              }}
            >
              {t(`閉じる`)}
            </div>
          )}
        </>
      )}
    </div>
  )
}

const User = ({
  salesforceId,
  fallbackName,
}: {
  salesforceId: string | undefined
  fallbackName: string
}): ReactElement => {
  const getSalesforceUser = useAppSelector(selectGetSalesforceUser)

  const resourceUser = salesforceId === undefined ? undefined : getSalesforceUser(salesforceId)
  const user = resourceUser ?? {
    id: ``,
    name: fallbackName,
  }

  return (
    <div className="flex items-center gap-1">
      <span className="text-xs">{user.name}</span>
      <UserAvatar name={user.name} imageUrl="" size="small" />
    </div>
  )
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const getDeepKeyValues = (object: Record<string, unknown> | unknown[], prefix = ''): string[] => {
  const keyValues: string[] = []

  if (Array.isArray(object)) {
    for (const [index, item] of object.entries()) {
      const fullPath = `${prefix}[${index}]`
      if (typeof item === 'object' && item !== null) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        keyValues.push(...getDeepKeyValues(item as Record<string, unknown>, fullPath))
      } else {
        keyValues.push(`${fullPath}::${String(item)}`)
      }
    }
  } else {
    // eslint-disable-next-line guard-for-in
    for (const key in object) {
      const value = object[key]
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      const fullPath = prefix ? `${prefix}.${key}` : key
      if (typeof value === 'object' && value !== null) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        keyValues.push(...getDeepKeyValues(value as Record<string, unknown>, fullPath))
      } else {
        keyValues.push(`${fullPath}::${String(value)}`)
      }
    }
  }

  return keyValues
}

const SheetValueEmptyCell = ({
  column,
  rowIndex,
  columnIndex,
  innerRowIndex,
  defaultStyle,
  setDragging,
  expandOnDrag,
  query,
  ability,
}: {
  column: SheetColumn<ViewQueryRecordNode>
  rowIndex: number
  columnIndex: number
  innerRowIndex: number
  defaultStyle: CSSProperties
  setDragging: () => void
  expandOnDrag: (position: RSheetsCursorPosition) => void
  query: ViewQueryList
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  const { addChildRecord } = useAddEmptyRecordMutation(query, ability)
  const { expand, setContextMenuPosition, setMainCursor } = useCursorMutation(ability)
  const rows = useRecordsImpl()
  const { message } = App.useApp()
  const boxStyle: CSSProperties = {
    ...defaultStyle,
    fontSize: 14,
    padding: '10px 10px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    transition: RSheetsStyle.cellTransition,
    textAlign: 'center',
    color: RSheetsStyle.color.gray,
    // 空レコードのセルは nodeType によらず濃い色で統一する (別オブジェクトの parent/children も同様に塗る)
    backgroundColor: getEmptyRecordCellBackgroundColor(column.color),
  }

  const position = {
    columnIndex,
    rowIndex,
    innerRowIndex,
  }

  // 空セルでの子レコード作成が書き込み禁止 (列 readonly / 条件付き disable) かどうか。新規レコードの内容で判定 (キーボード経路と同じ)。
  const isEmptyCellWriteProhibited = (): boolean => {
    const parentRecordNode = Array.get(rows, rowIndex).pipe(Option.getOrUndefined)
    return parentRecordNode !== undefined && isCellWriteProhibited(column, parentRecordNode, query)
  }

  return (
    <>
      <div
        style={boxStyle}
        onPointerEnter={() => {
          expandOnDrag({ rowIndex, columnIndex, innerRowIndex })
        }}
        onClick={() => {
          if (column.nodeType === 'children' || column.nodeType === 'child') {
            if (isEmptyCellWriteProhibited()) {
              // boolean 列は MainCursorBox の pointerEvents:'none' で clickCurrentCursorMutation に到達しないため、他型が同経路で出す
              // 書き込み禁止警告が空セルで出ない。boolean だけここで補う (非 boolean は clickCurrentCursorMutation が出すので二重通知回避で対象外)。
              // 文言は clickCurrentCursorMutation に揃え、列 readonly は「このカラムは書き込み禁止です」、条件付き disable は「このセルは書き込み禁止です」。
              if (column.type === 'boolean') {
                void message.warning(
                  column.readonly ? t(`このカラムは書き込み禁止です`) : t(`このセルは書き込み禁止です`),
                )
              }
              return
            }
            void addChildRecord({ rowIndex, innerRowIndex, node: column.node, additionalFieldValues: [] })
          }
        }}
        onPointerDown={(event) => {
          setDragging() // event.stopPropagation()などによりsetDraggingが実行されなくなると、ドラッグによるセルの範囲選択が動作しなくなる。
          // ドラッグによるセルの範囲選択を実現するためには、クリック時にメインカーソルを更新する必要がある。
          // クリックを離す時に発火するonClickだと遅すぎるので、onPointerDownで処理する。
          if (event.shiftKey) {
            expand({
              rowIndex,
              columnIndex,
              innerRowIndex,
            })
            return
          }
          if (column.nodeType === 'children' || column.nodeType === 'child' || column.nodeType === 'parent') {
            setMainCursor(position)
          }
        }}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
        onContextMenu={async (e) => {
          const contextMenuPosition = {
            x: e.clientX,
            y: e.clientY,
          }
          setContextMenuPosition(position, contextMenuPosition)
          e.preventDefault()
          e.stopPropagation()

          // 2023/06 「空セルの上でも右クリックメニューが出せる」という仕様でがあると、ペースト側で空セルなのかどうかの判定が必要で、多少面倒
          //         そのため、右クリックのときも左クリックと同様にレコードを作成する挙動とする
          //         特に問題ないと考えているが、もし問題があればusePasteWithNewRecord側で空セル判定を行う
          if (column.nodeType === 'children' || column.nodeType === 'child') {
            setMainCursor(position)
            // 右クリックはコンテキストメニュー表示が主目的。書き込み禁止セルでは作成しないが通知は出さない
            if (!isEmptyCellWriteProhibited()) {
              await addChildRecord({ rowIndex, innerRowIndex, node: column.node, additionalFieldValues: [] })
            }
          }
        }}
      >
        {column.nodeType === 'parent' ? (
          <SearchOutlined />
        ) : // eslint-disable-next-line sonarjs/no-nested-conditional
        column.nodeType === 'children' ? (
          <PlusCircleOutlined />
        ) : (
          <></>
        )}
      </div>
    </>
  )
}
